import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export interface SubscriptionStatusResponse {
  status: string;
  tier_name: string | null;
  tier_code: string | null;
  days_remaining: number | null;
  trial_end: string | null;
  current_period_end: string | null;
  is_free: boolean;
  is_past_due: boolean;
  requires_payment_method: boolean;
  access_reason?: 'card_required' | 'trial_expired' | null;
}

/**
 * The organization's subscription state, shared by every consumer through one
 * react-query cache entry so the app never issues duplicate status calls or
 * shows two components disagreeing about whether the plan is paid.
 */
export const useSubscriptionStatus = () => {
  const { isAuthenticated, user } = useAuth();

  return useQuery<SubscriptionStatusResponse | null>({
    queryKey: ['subscription-status'],
    queryFn: async () => {
      const response = await api.get<SubscriptionStatusResponse>('/api/subscription/status/');
      return response.data;
    },
    enabled: Boolean(isAuthenticated && user?.organization),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
};

/**
 * Whether the org may take an action that starts a recurring outside charge —
 * today, renting a phone number. Mirrors ``subscription_is_paid`` in
 * backend/apexos/apps/core/permissions.py: a trial does NOT qualify, comped
 * (is_free) orgs do.
 *
 * Undefined while the status is still loading, so callers can avoid flashing
 * a disabled control before the answer is known. The backend is the real
 * enforcement; this only keeps users out of a dead end.
 */
export const useCanIncurCharges = (): boolean | undefined => {
  const { data, isLoading } = useSubscriptionStatus();
  if (isLoading || data === undefined) return undefined;
  if (data === null) return false;
  return data.is_free === true || data.status === 'active';
};
